import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Globe, Shield } from 'lucide-react';

// Dark background footer logo - uploaded by user for better visibility
const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_resite-upgrade/artifacts/rvqxb1u3_Shree%20Bajrang%20%285%29.png";

const RERA_NUMBER = "CGRERA100518A000114";

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Projects', href: '/projects' },
  { label: 'Plots', href: '/plots' },
  { label: 'Houses', href: '/houses' },
  { label: 'Commercial', href: '/commercial' },
];

const toolLinks = [
  { label: 'Land Converter', href: '/land-size-converter' },
  { label: 'Loan Calculator', href: '/loan-calculator' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
];

export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
        {/* Mobile-first stacked layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column - Logo first on mobile */}
          <div className="sm:col-span-2 lg:col-span-1 text-center sm:text-left">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={FOOTER_LOGO_URL} 
                alt="Shree Bajrang Developers" 
                className="h-12 sm:h-14 w-auto object-contain mx-auto sm:mx-0"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Trusted plots and homes Providers in Jamul–Bhilai region. Building trust since 2018.
            </p>
            {/* RERA Badge */}
            <div className="flex items-center justify-center sm:justify-start space-x-2 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-3 py-2 mb-3">
              <Shield className="h-4 w-4 text-brand-gold flex-shrink-0" />
              <div className="text-left">
                <p className="text-brand-gold font-medium text-xs">C.G. RERA Registered</p>
                <p className="text-gray-400 text-xs">Reg. No.: {RERA_NUMBER}</p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-center sm:text-left">
            <h4 className="font-heading font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm inline-block py-1"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tools & Resources */}
          <div className="text-center sm:text-left">
            <h4 className="font-heading font-semibold text-lg mb-4">Tools & Resources</h4>
            <ul className="space-y-2">
              {toolLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm inline-block py-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="sm:col-span-2 lg:col-span-1 text-center sm:text-left">
            <h4 className="font-heading font-semibold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="https://maps.app.goo.gl/ZZ6YU1F7tCe8F1tx6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start justify-center sm:justify-start space-x-3 text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-map-link"
                >
                  <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-left">Shivpuri, Jamul, Bhilai, District – Durg, Chhattisgarh 490024</span>
                </a>
              </li>
              <li>
                <a 
                  href="tel:7581061649"
                  className="flex items-center justify-center sm:justify-start space-x-3 text-gray-400 hover:text-white transition-colors text-sm py-1"
                >
                  <Phone className="h-5 w-5" />
                  <span>7581061649, 9131709809</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:contact@shreebajrangdevelopers.com"
                  className="flex items-center justify-center sm:justify-start space-x-3 text-gray-400 hover:text-white transition-colors text-sm py-1"
                >
                  <Mail className="h-5 w-5" />
                  <span>contact@shreebajrangdevelopers.com</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://shreebajrangdevelopers.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center sm:justify-start space-x-3 text-gray-400 hover:text-white transition-colors text-sm py-1"
                >
                  <Globe className="h-5 w-5" />
                  <span>shreebajrangdevelopers.com</span>
                </a>
              </li>
            </ul>

            {/* Social/Contact Icons - Centered on mobile */}
            <div className="flex items-center justify-center sm:justify-start space-x-3 mt-6">
              <a
                href="tel:7581061649"
                className="w-11 h-11 sm:w-10 sm:h-10 bg-brand-orange/20 rounded-full flex items-center justify-center hover:bg-brand-orange transition-colors"
                data-testid="footer-phone-icon"
              >
                <Phone className="h-5 w-5" />
              </a>
              <a
                href="https://wa.me/9131709809"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 sm:w-10 sm:h-10 bg-green-500/20 rounded-full flex items-center justify-center hover:bg-green-500 transition-colors"
                data-testid="footer-whatsapp-icon"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
              <a
                href="mailto:contact@shreebajrangdevelopers.com"
                className="w-11 h-11 sm:w-10 sm:h-10 bg-blue-500/20 rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors"
              >
                <Mail className="h-5 w-5" />
              </a>
              <a
                href="https://maps.app.goo.gl/AKkfXSn86bEp4kA48"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 sm:w-10 sm:h-10 bg-red-500/20 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* RERA Registration Banner */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 text-center">
            <Shield className="h-5 w-5 text-brand-gold" />
            <p className="text-gray-400 text-sm">
              <span className="text-brand-gold font-medium">C.G. RERA Registered</span> – Registration No.: <span className="text-white font-mono">{RERA_NUMBER}</span>
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-6 pt-6 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 text-center">
          <p>© {new Date().getFullYear()} Shree Bajrang Developers. All rights reserved.</p>
          <p className="mt-2 md:mt-0">Building dreams since 2018</p>
        </div>
      </div>
    </footer>
  );
};
